/* ===========================================================================
   Atrieye theme scripts. No external libraries.
   ========================================================================= */
(function () {
  'use strict';

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------- scroll reveal ----- */
  function initReveal() {
    var items = document.querySelectorAll('.reveal:not(.is-visible)');
    if (!items.length) return;

    if (!('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
    );

    items.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* --------------------------------------- slow zoom while scrolling --- */
  /**
   * Frames marked data-scroll-zoom scale their image gently as the frame
   * travels through the viewport. Driven by a single rAF-throttled scroll
   * listener, and skipped entirely under prefers-reduced-motion.
   */
  function initScrollZoom() {
    if (reduceMotion) return;
    var frames = Array.prototype.slice.call(document.querySelectorAll('[data-scroll-zoom]'));
    if (!frames.length) return;

    var visible = [];
    var ticking = false;
    var MAX_ZOOM = 0.12; // 1.00 -> 1.12

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var i = visible.indexOf(entry.target);
            if (entry.isIntersecting && i === -1) visible.push(entry.target);
            if (!entry.isIntersecting && i !== -1) visible.splice(i, 1);
          });
          update();
        },
        { rootMargin: '10% 0px 10% 0px' }
      );
      frames.forEach(function (f) {
        io.observe(f);
      });
    } else {
      visible = frames;
    }

    function update() {
      ticking = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      visible.forEach(function (frame) {
        var rect = frame.getBoundingClientRect();
        // 0 when the frame's top first enters from below, 1 when it leaves the top
        var progress = (vh - rect.top) / (vh + rect.height);
        if (progress < 0) progress = 0;
        if (progress > 1) progress = 1;
        frame.style.setProperty('--zoom', (1 + progress * MAX_ZOOM).toFixed(4));
      });
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ------------------------------------------------- mobile nav -------- */
  function initMobileNav() {
    var toggle = document.querySelector('[data-nav-toggle]');
    var nav = document.querySelector('[data-mobile-nav]');
    if (!toggle || !nav) return;

    toggle.addEventListener('click', function () {
      var isOpen = nav.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  /* ------------------------------------------------ announcement ------- */
  function initAnnouncement() {
    var bar = document.querySelector('[data-announcement]');
    if (!bar) return;

    var key = 'atrieye-announcement-' + (bar.getAttribute('data-announcement-key') || '1');

    if (bar.hasAttribute('data-dismissible')) {
      var dismissed = false;
      try {
        dismissed = window.localStorage.getItem(key) === 'dismissed';
      } catch (e) {
        dismissed = false;
      }
      var inEditor = !!(window.Shopify && window.Shopify.designMode);
      if (dismissed && !inEditor) {
        bar.hidden = true;
        return;
      }

      var close = bar.querySelector('[data-announcement-close]');
      if (close) {
        close.addEventListener('click', function () {
          bar.hidden = true;
          try {
            window.localStorage.setItem(key, 'dismissed');
          } catch (e) {
            /* private mode, nothing to do */
          }
        });
      }
    }

    // Rotate through multiple messages
    var slides = bar.querySelectorAll('.announcement__slide');
    if (slides.length > 1 && !reduceMotion) {
      var interval = parseInt(bar.getAttribute('data-rotate-seconds'), 10) || 6;
      var index = 0;
      window.setInterval(function () {
        slides[index].classList.remove('is-active');
        index = (index + 1) % slides.length;
        slides[index].classList.add('is-active');
      }, interval * 1000);
    }
  }

  /* --------------------------------------- reserve -> waitlist jump ---- */
  function initReserveButtons() {
    var buttons = document.querySelectorAll('[data-reserve-target]');
    if (!buttons.length) return;

    buttons.forEach(function (button) {
      button.addEventListener('click', function (event) {
        var form = document.querySelector('[data-waitlist-form]');
        if (!form) return; // no waitlist on this page; let the link behave normally

        event.preventDefault();

        var slug = button.getAttribute('data-reserve-target');
        var select = form.querySelector('[data-waitlist-interest]');

        if (select && slug) {
          for (var i = 0; i < select.options.length; i++) {
            if (select.options[i].value === slug) {
              select.selectedIndex = i;
              break;
            }
          }
        }

        var section = form.closest('.section') || form;
        section.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });

        var nameField = form.querySelector('[data-waitlist-name]');
        if (nameField) {
          window.setTimeout(function () {
            try {
              nameField.focus({ preventScroll: true });
            } catch (e) {
              nameField.focus();
            }
          }, 500);
        }
      });
    });
  }

  /* --------------------------------------------- waitlist submit ------- */
  /**
   * Shopify's lightweight `customer` form reliably persists only the email
   * and the tags to a real, exportable Customer record. Extra fields such as
   * contact[first_name] are unreliable and can even error, so the visitor's
   * name and their product interest are folded into contact[tags] here,
   * where they are guaranteed to survive and stay filterable in admin.
   * Without JS the form still submits with the base tag.
   */
  function sanitizeTag(value) {
    return value.replace(/,/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 60);
  }

  function initWaitlistForms() {
    var forms = document.querySelectorAll('[data-waitlist-form]');
    if (!forms.length) return;

    forms.forEach(function (form) {
      form.addEventListener('submit', function () {
        var tagsField = form.querySelector('[data-waitlist-tags]');
        if (!tagsField) return;

        var tags = [];
        var baseTag = tagsField.getAttribute('data-base-tag') || 'preorder-waitlist';
        tags.push(baseTag);

        var select = form.querySelector('[data-waitlist-interest]');
        if (select && select.value) {
          tags.push('interest-' + sanitizeTag(select.value));
        }

        var nameField = form.querySelector('[data-waitlist-name]');
        if (nameField && nameField.value.trim()) {
          tags.push('Name: ' + sanitizeTag(nameField.value));
        }

        tagsField.value = tags.join(', ');
      });
    });
  }

  /* ------------------------------------------- live variant picker ----- */
  /**
   * Each <option> carries its variant id plus price strings already
   * formatted server-side by Liquid's `money` filter, so switching a variant
   * never needs a fetch or any currency maths in the browser.
   */
  function initVariantPickers(root) {
    var scope = root || document;
    var selects = scope.querySelectorAll('[data-variant-select]');
    if (!selects.length) return;

    selects.forEach(function (select) {
      if (select.dataset.bound === 'true') return;
      select.dataset.bound = 'true';

      select.addEventListener('change', function () {
        var option = select.options[select.selectedIndex];
        var container = select.closest('[data-product-scope]');
        if (!option || !container) return;

        var soldOut = option.getAttribute('data-sold-out') === 'true';

        var idInput = container.querySelector('[data-variant-id]');
        if (idInput) idInput.value = option.value;

        var button = container.querySelector('[data-add-button]');
        if (button) {
          button.disabled = soldOut;
          button.textContent = soldOut
            ? button.getAttribute('data-sold-out-text') || 'Sold out'
            : button.getAttribute('data-add-text') || 'Add to cart';
        }

        var priceEl = container.querySelector('[data-price-display]');
        if (priceEl) {
          var price = option.getAttribute('data-price') || '';
          var compareAt = option.getAttribute('data-compare-at') || '';
          priceEl.innerHTML = compareAt
            ? '<s>' + compareAt + '</s><span>' + price + '</span>'
            : '<span>' + price + '</span>';
        }
              var mainFrame = container.querySelector('[data-main-image]');
      if (mainFrame && window.__variantImages__) {
        var imgData = window.__variantImages__[option.value];
        if (imgData && imgData.src) {
          var img = mainFrame.querySelector('img');
          if (img) {
            img.src = imgData.src;
            img.srcset = imgData.srcset || '';
            img.alt = imgData.alt || '';
          }
        }
      }
      });
    });
  }

  /* ------------------------------------------------------ slideshow ---- */
  function initSlideshows() {
    var shows = document.querySelectorAll('[data-slideshow]');
    if (!shows.length) return;

    shows.forEach(function (show) {
      if (show.dataset.bound === 'true') return;
      show.dataset.bound = 'true';

      var slides = show.querySelectorAll('.slideshow__slide');
      var dots = show.querySelectorAll('.slideshow__dot');
      if (slides.length < 2) return;

      var index = 0;
      var timer = null;
      var autoplay = show.hasAttribute('data-autoplay');
      var delay = (parseInt(show.getAttribute('data-autoplay'), 10) || 6) * 1000;

      function go(next) {
        slides[index].classList.remove('is-active');
        if (dots[index]) dots[index].classList.remove('is-active');
        index = (next + slides.length) % slides.length;
        slides[index].classList.add('is-active');
        if (dots[index]) dots[index].classList.add('is-active');
      }

      function start() {
        if (!autoplay || reduceMotion) return;
        stop();
        timer = window.setInterval(function () {
          go(index + 1);
        }, delay);
      }

      function stop() {
        if (timer) window.clearInterval(timer);
        timer = null;
      }

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          go(i);
          start();
        });
      });

      var prev = show.querySelector('[data-slide-prev]');
      var next = show.querySelector('[data-slide-next]');
      if (prev)
        prev.addEventListener('click', function () {
          go(index - 1);
          start();
        });
      if (next)
        next.addEventListener('click', function () {
          go(index + 1);
          start();
        });

      show.addEventListener('mouseenter', stop);
      show.addEventListener('mouseleave', start);
      show.addEventListener('focusin', stop);

      start();
    });
  }

  /* ----------------------------------------------------- countdown ----- */
  function initCountdowns() {
    var clocks = document.querySelectorAll('[data-countdown]');
    if (!clocks.length) return;

    clocks.forEach(function (clock) {
      var target = Date.parse(clock.getAttribute('data-countdown'));
      if (isNaN(target)) return;

      var out = clock.querySelector('[data-countdown-clock]');
      var expired = clock.querySelector('[data-countdown-expired]');

      function pad(n) {
        return n < 10 ? '0' + n : String(n);
      }

      function tick() {
        var diff = target - Date.now();
        if (diff <= 0) {
          if (out) out.hidden = true;
          if (expired) expired.hidden = false;
          return;
        }
        var s = Math.floor(diff / 1000);
        var days = Math.floor(s / 86400);
        var hours = Math.floor((s % 86400) / 3600);
        var mins = Math.floor((s % 3600) / 60);
        var secs = s % 60;

        var map = { days: days, hours: hours, minutes: mins, seconds: secs };
        Object.keys(map).forEach(function (unit) {
          var el = clock.querySelector('[data-unit="' + unit + '"]');
          if (el) el.textContent = pad(map[unit]);
        });
        window.setTimeout(tick, 1000);
      }

      tick();
    });
  }

  /* ---------------------------------------------------------- FAQ ------ */
  function initAccordions() {
    var questions = document.querySelectorAll('[data-faq-question]');
    if (!questions.length) return;

    questions.forEach(function (q) {
      if (q.dataset.bound === 'true') return;
      q.dataset.bound = 'true';

      q.addEventListener('click', function () {
        var answer = document.getElementById(q.getAttribute('aria-controls'));
        if (!answer) return;
        var open = q.getAttribute('aria-expanded') === 'true';

        q.setAttribute('aria-expanded', open ? 'false' : 'true');
        answer.style.maxHeight = open ? null : answer.scrollHeight + 'px';
      });
    });
  }

  /* ----------------------------------------------------- lookbook ------ */
  function initLookbooks() {
    var spots = document.querySelectorAll('[data-hotspot]');
    if (!spots.length) return;

    spots.forEach(function (spot) {
      if (spot.dataset.bound === 'true') return;
      spot.dataset.bound = 'true';

      spot.addEventListener('click', function (event) {
        event.stopPropagation();
        var open = spot.getAttribute('aria-expanded') === 'true';
        document.querySelectorAll('[data-hotspot]').forEach(function (other) {
          other.setAttribute('aria-expanded', 'false');
        });
        spot.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    });

    document.addEventListener('click', function () {
      document.querySelectorAll('[data-hotspot]').forEach(function (spot) {
        spot.setAttribute('aria-expanded', 'false');
      });
    });
  }

  /* --------------------------------------------------- quick view ------ */
  /**
   * Fetches the product page and lifts its product form out of the markup,
   * so quick view always matches the real product page without duplicating
   * any pricing or variant logic.
   */
  function initQuickView() {
    var modal = document.getElementById('QuickView');
    if (!modal) return;

    var dialog = modal.querySelector('[data-quick-view-body]');
    var lastFocus = null;

    function open() {
      modal.classList.add('is-open');
      document.body.classList.add('no-scroll');
      var close = modal.querySelector('[data-quick-view-close]');
      if (close) close.focus();
    }

    function close() {
      modal.classList.remove('is-open');
      document.body.classList.remove('no-scroll');
      if (dialog) dialog.innerHTML = '';
      if (lastFocus) lastFocus.focus();
    }

    document.addEventListener('click', function (event) {
      if (!event.target || typeof event.target.closest !== 'function') return;

      var trigger = event.target.closest('[data-quick-view-url]');
      if (trigger) {
        event.preventDefault();
        lastFocus = trigger;
        if (dialog) {
          dialog.innerHTML = '<p class="quick-view__loading">Loading&hellip;</p>';
        }
        open();

        fetch(trigger.getAttribute('data-quick-view-url'))
          .then(function (r) {
            if (!r.ok) throw new Error('Network error');
            return r.text();
          })
          .then(function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var content = parsed.querySelector('.product-layout');
            if (content && dialog) {
              dialog.innerHTML = content.innerHTML;
              initVariantPickers(dialog);
            } else if (dialog) {
              dialog.innerHTML =
                '<p class="quick-view__loading">Open the full product page for details.</p>';
            }
          })
          .catch(function () {
            if (dialog) {
              dialog.innerHTML =
                '<p class="quick-view__loading">Sorry, that could not be loaded.</p>';
            }
          });
        return;
      }

      if (event.target.closest('[data-quick-view-close]') || event.target.closest('[data-quick-view-backdrop]')) {
        close();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && modal.classList.contains('is-open')) close();
    });
  }

  /* ------------------------------------------------- stock counter ----- */
  function initStockCounters() {
    var bars = document.querySelectorAll('[data-stock-fill]');
    bars.forEach(function (bar) {
      var pct = parseFloat(bar.getAttribute('data-stock-fill')) || 0;
      if (pct < 0) pct = 0;
      if (pct > 100) pct = 100;
      window.setTimeout(function () {
        bar.style.width = pct + '%';
      }, 200);
    });
  }

  function init() {
    initReveal();
    initScrollZoom();
    initMobileNav();
    initAnnouncement();
    initReserveButtons();
    initWaitlistForms();
    initVariantPickers();
    initSlideshows();
    initCountdowns();
    initAccordions();
    initLookbooks();
    initQuickView();
    initStockCounters();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders sections without a page load
  document.addEventListener('shopify:section:load', init);
})();
